import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { IMG } from "@/lib/assets";
import { PORTRAITS } from "@/lib/portraits";
import { ERLEBNISRAEUME } from "@/lib/erlebnisraeume";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { TESTIDS } from "@/constants/testIds";
import Reveal from "@/components/ui-brand/Reveal";
import Marquee from "@/components/ui-brand/Marquee";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import PhotoPlaceholder from "@/components/ui-brand/PhotoPlaceholder";
import Erlebnisraum from "@/components/ui-brand/Erlebnisraum";

const HomePage = () => {
  const { lang } = useLanguage();
  useDocumentMeta("studio");
  const [openRoom, setOpenRoom] = useState(null);

  return (
    <>
      {/* ============= NEW FULL-BLEED HEADER IMAGE ============= */}
      <section
        data-testid="home-header-image"
        className="relative w-full overflow-hidden bg-depth"
        style={{ marginTop: "-7rem" }}
      >
        <img
          src={IMG.studioHeader}
          alt="patrice.studio"
          className="block w-full h-auto"
        />
      </section>

      {/* ============= HERO ============= */}
      <section
        data-testid={TESTIDS.homeHero}
        className="relative px-6 md:px-12 lg:px-16 pt-16 md:pt-24 lg:pt-28 pb-24 md:pb-32"
      >
        <PeonyMark
          size={780}
          opacity={0.05}
          className="absolute -right-32 -top-20 hidden md:block pointer-events-none"
        />

        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="col-span-12 lg:col-span-7 relative z-10">
            <Reveal delay={120}>
              <h1 className="headline-hero mt-2">
                {pick(t.home.headline_line_1, lang)}
                <br />
                <span className="italic-serif text-atmosphere">
                  {pick(t.home.headline_line_2, lang)}
                </span>
              </h1>
            </Reveal>

            <Reveal delay={400}>
              <p className="body-lux-lg max-w-xl mt-12">{pick(t.home.lede, lang)}</p>
            </Reveal>

            <Reveal delay={500}>
              <div
                data-testid="home-trust-strip"
                className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 max-w-2xl"
              >
                <span className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere/60">
                  — {pick(t.home.trust_strip.label, lang)}
                </span>
                {t.home.trust_strip.items.map((item, i) => (
                  <React.Fragment key={i}>
                    <span className="h-px w-5 bg-warmth/60 hidden sm:inline-block" aria-hidden />
                    <span className="font-mono text-[0.62rem] tracking-[0.28em] uppercase text-depth/85">
                      {pick(item, lang)}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </Reveal>

            <Reveal delay={620}>
              <div className="mt-12 flex flex-wrap items-center gap-5">
                <Link to="/projekte" data-testid={TESTIDS.homeCtaPrimary} className="btn-primary">
                  {pick(t.home.cta_primary, lang)}
                  <span aria-hidden>↗</span>
                </Link>
                <Link to="/kontakt" data-testid={TESTIDS.homeCtaGhost} className="btn-ghost">
                  {pick(t.home.cta_ghost, lang)}
                </Link>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — clean peony image, no overlays, no text */}
          <div className="col-span-12 lg:col-span-5 relative">
            <Reveal>
              <div className="relative aspect-[4/5] overflow-hidden bg-depth">
                <img
                  src={IMG.peonyBouquetDark}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover animate-slowpan"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============= MANIFESTO ============= */}
      <section
        data-testid={TESTIDS.homeManifesto}
        className="relative px-6 md:px-12 lg:px-24 py-28 md:py-40"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-3">
            <Reveal>
              <p className="eyebrow">— {pick(t.home.manifesto.label, lang)}</p>
              <p className="font-mono text-[0.62rem] tracking-[0.28em] text-atmosphere/60 mt-4">
                01 / 05
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-9 lg:pl-12 lg:border-l border-warmth/40">
            <Reveal>
              <h2 className="headline-display text-depth max-w-3xl">
                <span className="italic-serif">„</span>
                {pick(t.home.manifesto.title, lang)}
                <span className="italic-serif">&ldquo;</span>
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="body-lux-lg mt-10 max-w-2xl">
                {pick(t.home.manifesto.body, lang)}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============= PILLARS ============= */}
      <section
        data-testid={TESTIDS.homePillars}
        className="relative bg-soft_canvas px-6 md:px-12 lg:px-24 py-28 md:py-36 border-y border-warmth/30 overflow-hidden"
      >
        <PeonyMark
          size={520}
          opacity={0.06}
          className="absolute -left-32 top-12 pointer-events-none"
        />
        <div className="grid grid-cols-12 gap-6 lg:gap-12 relative">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <p className="eyebrow">02 / 05 — Werte</p>
              <h2 className="headline-display mt-6 text-depth">
                {pick(t.home.pillars.title, lang)}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-8 lg:pl-8">
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
              {t.home.pillars.items.map((item, i) => (
                <Reveal key={i} delay={i * 60}>
                  <li className="border-l border-warmth pl-4">
                    <span className="font-mono text-[0.62rem] tracking-[0.28em] uppercase text-atmosphere/70">
                      0{i + 1}
                    </span>
                    <p className="font-display text-2xl mt-2 text-depth">
                      {pick(item, lang)}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============= UNIVERSE TEASER ============= */}
      <section
        data-testid={TESTIDS.homeUniverse}
        className="relative px-6 md:px-12 lg:px-24 py-28 md:py-40"
      >
        <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-16 md:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <p className="eyebrow">03 / 05 — {pick(t.home.universe.eyebrow, lang)}</p>
              <h2 className="headline-display mt-6 max-w-2xl">
                {pick(t.home.universe.title, lang)}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-16">
            <Reveal delay={160}>
              <p className="body-lux">{pick(t.home.universe.lede, lang)}</p>
              <Link to="/projekte" className="link-underline mt-8 inline-block font-mono text-[0.72rem] tracking-[0.32em] uppercase text-depth">
                {pick(t.nav.projects, lang)} ↗
              </Link>
            </Reveal>
          </div>
        </div>

        {/* Universe tetris removed — content now lives in Erlebnisräume below + dedicated Projekte page */}
      </section>

      {/* ============= ERLEBNISRÄUME ============= */}
      <section
        data-testid="home-erlebnisraeume"
        className="relative px-6 md:px-12 lg:px-24 py-28 md:py-40 border-t border-warmth/30 overflow-hidden"
      >
        <PeonyMark size={620} opacity={0.04} className="absolute -left-40 top-10 pointer-events-none" />

        <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-16 md:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <p className="eyebrow">04 / 05 — {pick(t.home.erleben.eyebrow, lang)}</p>
              <h2 className="headline-display mt-6 max-w-3xl">
                <span className="italic-serif text-atmosphere">
                  {pick(t.home.erleben.title, lang).split(",")[0]},
                </span>{" "}
                {pick(t.home.erleben.title, lang).split(",").slice(1).join(",").trim()}
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pt-16">
            <Reveal delay={160}>
              <p className="body-lux">
                {pick(t.home.erleben.lede, lang)}
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6 md:grid-rows-[420px_300px_380px]">
          {ERLEBNISRAEUME.map((room, idx) => {
            // Asymmetric magazine-collage layout — each room has a deliberate size.
            // Mobile: stacked. Desktop: editorial grid (hero left, two side-by-side, full-width banner).
            const layouts = [
              // 0 · Foto — tall hero (left, 2 rows)
              {
                col: "col-span-12 md:col-span-5 md:row-span-2",
                height: "h-[460px] md:h-full",
                size: "hero",
              },
              // 1 · Kunst — wide top right
              {
                col: "col-span-12 md:col-span-7 md:row-span-1",
                height: "h-[380px] md:h-full",
                size: "wide",
              },
              // 2 · Klang — small middle-right left
              {
                col: "col-span-12 sm:col-span-7 md:col-span-3",
                height: "h-[300px]",
                size: "small",
              },
              // 3 · Kreativ — small middle-right right
              {
                col: "col-span-12 sm:col-span-5 md:col-span-4",
                height: "h-[300px]",
                size: "small",
              },
              // 4 · Atelier — full-width editorial banner
              {
                col: "col-span-12",
                height: "h-[340px] md:h-[380px]",
                size: "banner",
              },
            ];
            const layout = layouts[idx] || layouts[0];
            const isHero = layout.size === "hero";
            const isBanner = layout.size === "banner";
            const isWide = layout.size === "wide";

            const baseGradient =
              idx % 2 === 0
                ? "linear-gradient(170deg, #28001B 0%, #5C4752 100%)"
                : "linear-gradient(170deg, #CBB7B0 0%, #5C4752 110%)";
            const peonyColor = idx % 2 === 0 ? "#FAF7F2" : "#28001B";
            const isComingSoon = room.state === "coming_soon";

            return (
              <Reveal
                key={room.id}
                delay={idx * 90}
                className={layout.col}
              >
                <button
                  type="button"
                  onClick={() => setOpenRoom(room)}
                  data-testid={`erlebnisraum-open-${room.id}`}
                  className={`group relative w-full text-left overflow-hidden block ${layout.height} bg-depth`}
                >
                  {/* Accent top hairline – the room's own colour signature */}
                  <span
                    className="absolute top-0 left-0 right-0 z-[5] h-[2px] origin-left transition-transform duration-700 ease-out scale-x-[0.18] group-hover:scale-x-100"
                    style={{ background: room.accent?.hex || "#FF7100" }}
                    aria-hidden
                  />

                  {/* Peony image background */}
                  {room.image && (
                    <img
                      src={room.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]"
                    />
                  )}
                  <div
                    className="absolute inset-0 mix-blend-multiply"
                    style={{ background: baseGradient, opacity: 0.55 }}
                    aria-hidden
                  />
                  <div className="absolute inset-0 grain-light pointer-events-none" />
                  <PeonyMark
                    size={isHero ? 460 : isBanner ? 380 : 240}
                    color={peonyColor}
                    opacity={0.07}
                    className={`absolute ${isHero ? "-right-24 -bottom-24" : isBanner ? "-right-20 -top-16" : "-right-10 -bottom-10"}`}
                  />

                  {/* Editorial crop marks */}
                  <span className="absolute top-5 left-5 w-3 h-3 border-l border-t border-canvas/60" />
                  <span className="absolute top-5 right-5 w-3 h-3 border-r border-t border-canvas/60" />
                  <span className="absolute bottom-5 left-5 w-3 h-3 border-l border-b border-canvas/60" />
                  <span className="absolute bottom-5 right-5 w-3 h-3 border-r border-b border-canvas/60" />

                  <div className="absolute top-7 left-7 right-7 flex items-center justify-between gap-3 text-canvas/85 font-mono text-[0.58rem] tracking-[0.32em] uppercase">
                    <span className="flex items-center gap-2.5">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ background: room.accent?.hex || "#FF7100" }}
                        aria-hidden
                      />
                      {lang === "de" ? "Raum" : "Room"} N° {room.number}
                    </span>
                    {isComingSoon && (
                      <span className="text-warmth border border-warmth/60 px-2 py-0.5 text-[0.5rem]">
                        {lang === "de" ? "Im Entstehen" : "In the making"}
                      </span>
                    )}
                  </div>

                  <div
                    className={`absolute inset-x-0 bottom-0 ${isHero || isBanner ? "p-6 md:p-10" : "p-5 md:p-6"} bg-gradient-to-t from-depth via-depth/70 to-transparent text-canvas`}
                  >
                    <span className="eyebrow-light">
                      {lang === "de" ? "Erlebnisraum" : "Inner room"} · N° {room.number}
                    </span>
                    <h3
                      className={`font-display leading-tight mt-3 ${
                        isHero
                          ? "text-3xl md:text-4xl lg:text-5xl"
                          : isBanner || isWide
                          ? "text-2xl md:text-3xl lg:text-4xl"
                          : "text-2xl md:text-[1.6rem]"
                      }`}
                    >
                      <span className="italic-serif">
                        {pick(room.name, lang).split(" ")[0]}
                      </span>
                      {pick(room.name, lang).split(" ").slice(1).length > 0 && (
                        <> {pick(room.name, lang).split(" ").slice(1).join(" ")}</>
                      )}
                    </h3>
                    <p
                      className={`mt-3 font-light text-warmth/90 max-w-md ${
                        isHero || isBanner ? "text-sm md:text-base" : "text-xs md:text-[0.78rem] leading-relaxed"
                      }`}
                    >
                      {pick(room.teaser, lang)}
                    </p>
                    <div
                      className={`mt-6 flex items-center gap-3 font-mono tracking-[0.32em] uppercase text-warmth group-hover:text-canvas transition-colors ${
                        isHero || isBanner ? "text-[0.66rem]" : "text-[0.58rem]"
                      }`}
                    >
                      <span>{lang === "de" ? "Raum öffnen" : "Open the room"}</span>
                      <span
                        aria-hidden
                        className="transition-transform duration-700 group-hover:translate-x-1"
                      >
                        ↗
                      </span>
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={260}>
          <div className="mt-12 md:mt-16" aria-hidden />
        </Reveal>
      </section>
      <section
        data-testid={TESTIDS.homeAboutTeaser}
        className="relative bg-soft_canvas px-6 md:px-12 lg:px-24 py-28 md:py-40 border-t border-warmth/30 overflow-hidden"
      >
        <PeonyMark size={620} opacity={0.025} className="absolute -right-40 -top-20 pointer-events-none" />

        <div className="grid grid-cols-12 gap-6 lg:gap-16">
          <Reveal className="col-span-12 lg:col-span-5">
            <PhotoPlaceholder
              testId="home-teaser-portrait"
              src={PORTRAITS.homeTeaser.src}
              alt={PORTRAITS.homeTeaser.alt}
              caption={PORTRAITS.homeTeaser.caption}
              label={PORTRAITS.homeTeaser.label}
              aspect={PORTRAITS.homeTeaser.aspect}
              tone={PORTRAITS.homeTeaser.tone}
            />
          </Reveal>

          <div className="col-span-12 lg:col-span-7 lg:pl-8 flex flex-col justify-center">
            <Reveal>
              <p className="eyebrow">05 / 05 — {pick(t.home.about_teaser.eyebrow, lang)}</p>
              <h2 className="headline-display mt-6 max-w-2xl">
                <span className="italic-serif">„</span>
                {pick(t.home.about_teaser.title, lang)}
                <span className="italic-serif">&ldquo;</span>
              </h2>
            </Reveal>
            <Reveal delay={320}>
              <p className="body-lux-lg mt-10 max-w-xl">
                {pick(t.home.about_teaser.body, lang)}
              </p>
            </Reveal>
            <Reveal delay={420}>
              <Link to="/ueber-mich" className="btn-ghost mt-12 w-fit">
                {pick(t.home.about_teaser.cta, lang)}
                <span aria-hidden>↗</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Erlebnisraum room={openRoom} onClose={() => setOpenRoom(null)} />
    </>
  );
};

export default HomePage;
